/**
 * Voice AI — Calls Shared Styles
 * Shared CSS for all calls/ sub-components.
 */

import { css } from 'lit';

export const callsStyles = css`

  /* ─── Page Header ─── */
  .page-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: var(--space-8);
  }

  .page-header-text {
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
  }

  .page-title {
    font-family: var(--font-display);
    font-size: var(--text-4xl);
    font-weight: var(--font-black);
    color: var(--color-text-primary);
    letter-spacing: -0.04em;
  }

  .page-description {
    font-family: var(--font-body);
    font-size: var(--text-sm);
    color: var(--color-text-tertiary);
  }

  /* ─── Search & Filter Row ─── */
  .filter-row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--space-3);
  }

  .search-wrapper {
    position: relative;
    flex: 1;
    min-width: 260px;
  }

  .search-wrapper .search-icon {
    position: absolute;
    left: var(--space-3);
    top: 50%;
    transform: translateY(-50%);
    color: var(--color-text-tertiary);
    font-size: var(--text-sm);
    pointer-events: none;
  }

  .search-wrapper input {
    width: 100%;
    padding: var(--space-2) var(--space-3) var(--space-2) var(--space-10);
    background: var(--color-bg-secondary);
    border: 1px solid var(--color-border-primary);
    border-radius: var(--radius-md);
    color: var(--color-text-primary);
    font-family: var(--font-body);
    font-size: var(--text-sm);
    line-height: var(--leading-normal);
    transition: all var(--transition-fast);
  }

  .search-wrapper input:focus {
    outline: none;
    border-color: var(--color-primary);
    box-shadow: var(--shadow-glow-primary);
  }

  .search-wrapper input::placeholder {
    color: var(--color-text-tertiary);
  }

  .filter-group {
    display: flex;
    align-items: center;
    gap: var(--space-2);
  }

  .filter-group-label {
    font-size: var(--text-xs);
    font-weight: var(--font-semibold);
    color: var(--color-text-tertiary);
    text-transform: uppercase;
    letter-spacing: var(--tracking-wider);
    white-space: nowrap;
  }

  .chip {
    display: inline-flex;
    align-items: center;
    gap: var(--space-1);
    padding: var(--space-1) var(--space-3);
    border-radius: var(--radius-full);
    font-family: var(--font-body);
    font-size: var(--text-xs);
    font-weight: var(--font-medium);
    cursor: pointer;
    transition: all var(--transition-base);
    border: 1px solid var(--color-border-primary);
    background: var(--color-bg-secondary);
    color: var(--color-text-secondary);
    white-space: nowrap;
    user-select: none;
  }

  .chip:hover {
    border-color: var(--color-border-secondary);
    background: var(--color-bg-tertiary);
  }

  .chip.active {
    background: var(--color-primary);
    color: var(--color-text-inverse);
    border-color: var(--color-primary);
  }

  .chip.active:hover {
    background: var(--color-primary-hover);
    border-color: var(--color-primary-hover);
  }

  /* ─── Active Filter Badges ─── */
  .active-filters {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-2);
  }

  .filter-badge {
    display: inline-flex;
    align-items: center;
    gap: var(--space-1);
    padding: 2px var(--space-2) 2px var(--space-3);
    background: var(--color-primary-subtle);
    color: var(--color-primary);
    border-radius: var(--radius-full);
    font-size: var(--text-xs);
    font-weight: var(--font-semibold);
  }

  .filter-badge .dismiss {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    border-radius: var(--radius-full);
    background: transparent;
    border: none;
    color: var(--color-primary);
    cursor: pointer;
    font-size: 10px;
    transition: background var(--transition-fast);
    padding: 0;
  }

  .filter-badge .dismiss:hover {
    background: var(--color-primary-light);
  }

  /* ─── Stats Summary Bar ─── */
  .stats-bar {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: var(--space-3);
  }

  .mini-stat {
    background: rgba(255, 255, 255, 0.18);
    backdrop-filter: blur(8px) saturate(1.2);
    -webkit-backdrop-filter: blur(8px) saturate(1.2);
    border: 1px solid rgba(255, 255, 255, 0.35);
    border-radius: var(--radius-xl);
    padding: var(--space-6);
    display: flex;
    align-items: center;
    gap: var(--space-3);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.5), inset 0 -1px 0 rgba(255, 255, 255, 0.08);
    transition: all var(--transition-base);
    position: relative;
    overflow: hidden;
  }

  .mini-stat:hover {
    box-shadow: var(--shadow-md);
    border-color: var(--color-border-secondary);
  }

  .mini-stat-icon {
    width: 36px;
    height: 36px;
    border-radius: var(--radius-md);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: var(--text-base);
    flex-shrink: 0;
  }

  .mini-stat-icon.calls {
    background: var(--color-primary-light);
    color: var(--color-primary);
  }
  .mini-stat-icon.completed {
    background: var(--color-success-light);
    color: var(--color-success);
  }
  .mini-stat-icon.duration {
    background: var(--color-accent-light);
    color: var(--color-accent);
  }
  .mini-stat-icon.minutes {
    background: var(--color-info-light);
    color: var(--color-info);
  }

  .mini-stat-content {
    display: flex;
    flex-direction: column;
    gap: 1px;
    min-width: 0;
  }

  .mini-stat-value {
    font-family: var(--font-display);
    font-size: var(--text-xl);
    font-weight: var(--font-bold);
    color: var(--color-text-primary);
    line-height: var(--leading-tight);
    letter-spacing: var(--tracking-tight);
  }

  .mini-stat-label {
    font-size: var(--text-xs);
    color: var(--color-text-tertiary);
    font-weight: var(--font-medium);
    text-transform: uppercase;
    letter-spacing: var(--tracking-wider);
  }

  /* ─── Table Card ─── */
  .table-card {
    background: rgba(255, 255, 255, 0.18);
    backdrop-filter: blur(8px) saturate(1.2);
    -webkit-backdrop-filter: blur(8px) saturate(1.2);
    border: 1px solid rgba(255, 255, 255, 0.35);
    border-radius: var(--radius-xl);
    overflow: hidden;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.5), inset 0 -1px 0 rgba(255, 255, 255, 0.08);
    position: relative;
  }

  .table-scroll {
    overflow-x: auto;
  }

  table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
    font-family: var(--font-body);
  }

  thead th {
    padding: var(--space-3) var(--space-4);
    text-align: left;
    font-size: var(--text-xs);
    font-weight: var(--font-semibold);
    color: var(--color-text-tertiary);
    text-transform: uppercase;
    letter-spacing: var(--tracking-wider);
    border-bottom: 1px solid var(--color-border-primary);
    background: var(--color-bg-tertiary);
    white-space: nowrap;
    user-select: none;
    position: sticky;
    top: 0;
    z-index: 2;
  }

  thead th.sortable {
    cursor: pointer;
    transition: color var(--transition-fast);
  }

  thead th.sortable:hover {
    color: var(--color-text-primary);
  }

  .sort-arrows {
    display: inline-flex;
    flex-direction: column;
    margin-left: var(--space-1);
    font-size: 8px;
    line-height: 1;
    vertical-align: middle;
    color: var(--color-text-disabled);
  }

  .sort-arrows .up,
  .sort-arrows .down {
    transition: color var(--transition-fast);
  }

  .sort-arrows .up.active,
  .sort-arrows .down.active {
    color: var(--color-primary);
  }

  tbody tr {
    transition: background var(--transition-fast);
    cursor: pointer;
  }

  tbody tr:nth-child(even) {
    background: rgba(245, 245, 244, 0.4);
  }

  tbody tr:hover {
    background: var(--color-bg-hover);
  }

  /* Skeleton rows — no interaction styling */
  tbody tr.sk-row {
    cursor: default;
    background: transparent !important;
  }
  tbody tr.sk-row:hover {
    background: transparent !important;
  }

  tbody tr.expanded-parent {
    background: var(--color-primary-light);
  }

  tbody td {
    padding: var(--space-3) var(--space-4);
    font-size: var(--text-sm);
    color: var(--color-text-secondary);
    border-bottom: 1px solid var(--color-border-subtle);
    vertical-align: middle;
  }

  .cell-datetime {
    display: flex;
    flex-direction: column;
    gap: 1px;
  }

  .cell-datetime .date {
    font-weight: var(--font-medium);
    color: var(--color-text-primary);
    font-size: var(--text-sm);
  }

  .cell-datetime .time {
    font-size: var(--text-xs);
    color: var(--color-text-tertiary);
  }

  .cell-phone {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    font-family: var(--font-mono);
    font-size: var(--text-sm);
    font-weight: var(--font-medium);
    color: var(--color-text-primary);
    letter-spacing: 0.02em;
  }

  .cell-phone-icon {
    font-size: var(--text-xs);
    color: var(--color-text-tertiary);
    flex-shrink: 0;
  }

  .cell-agent {
    display: flex;
    align-items: center;
    gap: var(--space-2);
  }

  .agent-avatar {
    width: 24px;
    height: 24px;
    border-radius: var(--radius-full);
    background: var(--color-primary-subtle);
    color: var(--color-primary);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    flex-shrink: 0;
  }

  .cell-duration {
    font-family: var(--font-mono);
    font-size: var(--text-sm);
    color: var(--color-text-primary);
  }

  .cell-direction {
    display: flex;
    align-items: center;
    gap: var(--space-1);
    font-size: var(--text-xs);
    font-weight: var(--font-medium);
  }

  .cell-direction.inbound {
    color: var(--color-accent);
  }

  .cell-direction.outbound {
    color: var(--color-info);
  }

  .cell-direction i {
    font-size: var(--text-sm);
  }

  /* ─── Transcript Count Badge ─── */
  .cell-transcripts {
    display: flex;
    align-items: center;
    gap: var(--space-2);
  }

  .transcript-count {
    display: inline-flex;
    align-items: center;
    gap: var(--space-1);
    padding: 2px var(--space-2);
    background: var(--color-bg-secondary);
    border: 1px solid var(--color-border-primary);
    border-radius: var(--radius-full);
    font-size: var(--text-xs);
    font-weight: var(--font-medium);
    color: var(--color-text-secondary);
  }

  .transcript-count.has-data {
    background: var(--color-primary-subtle);
    border-color: var(--color-primary-light);
    color: var(--color-primary);
  }

  .transcript-count i {
    font-size: 10px;
  }

  /* ─── Sentiment Bar (Table Cell) ─── */
  .cell-sentiment {
    display: flex;
    align-items: center;
    gap: var(--space-2);
  }

  .sentiment-bar {
    width: 80px;
    height: 6px;
    background: var(--color-bg-tertiary);
    border-radius: 3px;
    overflow: hidden;
    flex-shrink: 0;
  }

  .sentiment-fill {
    height: 100%;
    border-radius: 3px;
    transition: width var(--transition-base);
  }

  .sentiment-fill.positive {
    background: linear-gradient(90deg, #22C55E, #16A34A);
  }

  .sentiment-fill.neutral {
    background: linear-gradient(90deg, #F59E0B, #D97706);
  }

  .sentiment-fill.negative {
    background: linear-gradient(90deg, #EF4444, #DC2626);
  }

  .sentiment-fill.unknown {
    background: var(--color-border-secondary);
  }

  /* ─── Sentiment Chip (small colored pill label) ─── */
  .sentiment-chip {
    display: inline-flex;
    align-items: center;
    padding: 2px 8px;
    border-radius: 10px;
    font-size: 11px;
    font-weight: var(--font-semibold);
    line-height: 1.4;
    letter-spacing: 0.2px;
    white-space: nowrap;
  }

  .sentiment-chip.positive {
    background: rgba(34, 197, 94, 0.12);
    color: #16A34A;
    border: 1px solid rgba(34, 197, 94, 0.25);
  }

  .sentiment-chip.neutral {
    background: rgba(245, 158, 11, 0.12);
    color: #B45309;
    border: 1px solid rgba(245, 158, 11, 0.25);
  }

  .sentiment-chip.negative {
    background: rgba(239, 68, 68, 0.12);
    color: #DC2626;
    border: 1px solid rgba(239, 68, 68, 0.25);
  }

  .sentiment-chip.unknown {
    background: var(--color-bg-tertiary);
    color: var(--color-text-tertiary);
    border: 1px solid var(--color-border-primary);
  }

  /* ─── Action Buttons (Table Cell) ─── */
  .cell-actions {
    display: flex;
    align-items: center;
    gap: 2px;
  }

  .action-btn {
    width: 28px;
    height: 28px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: none;
    background: transparent;
    color: var(--color-text-tertiary);
    border-radius: var(--radius-md);
    cursor: pointer;
    font-size: var(--text-xs);
    transition: all var(--transition-fast);
    padding: 0;
  }

  .action-btn:hover:not(:disabled) {
    background: var(--color-bg-tertiary);
    color: var(--color-text-primary);
  }

  .action-btn.active {
    background: var(--color-primary-subtle);
    color: var(--color-primary);
  }

  .action-btn:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }

  /* ─── Expanded Row Detail ─── */
  .expanded-detail {
    background: var(--color-bg-tertiary);
    border-bottom: 1px solid var(--color-border-primary);
    box-shadow: var(--shadow-xs);
  }

  .expanded-detail td {
    padding: 0;
  }

  .detail-inner {
    padding: var(--space-6) var(--space-8);
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: var(--space-8);
    animation: slideDown var(--duration-base) var(--ease-out) both;
  }

  .detail-section {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }

  .detail-section-title {
    font-family: var(--font-display);
    font-size: var(--text-sm);
    font-weight: var(--font-semibold);
    color: var(--color-text-primary);
    display: flex;
    align-items: center;
    gap: var(--space-2);
  }

  .detail-section-title i {
    color: var(--color-primary);
    font-size: var(--text-sm);
  }

  /* ─── Transcript Messages ─── */
  .transcript-list {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
    max-height: 500px;
    overflow-y: auto;
    padding-right: var(--space-2);
  }

  .transcript-msg {
    display: flex;
    gap: var(--space-2);
    font-size: var(--text-sm);
    line-height: var(--leading-relaxed);
  }

  .transcript-msg .msg-timestamp {
    font-family: var(--font-mono);
    font-size: 10px;
    color: var(--color-text-tertiary);
    white-space: nowrap;
    min-width: 86px;
    flex-shrink: 0;
    padding-top: 3px;
    opacity: 0.7;
  }

  .transcript-msg .speaker-label {
    font-weight: var(--font-semibold);
    font-size: var(--text-xs);
    text-transform: uppercase;
    letter-spacing: var(--tracking-wider);
    min-width: 48px;
    flex-shrink: 0;
    padding-top: 2px;
  }

  .transcript-msg .speaker-label.customer {
    color: var(--color-accent);
  }

  .transcript-msg .speaker-label.agent {
    color: var(--color-primary);
  }

  .transcript-msg .msg-content {
    color: var(--color-text-secondary);
  }

  /* ─── Tool Calls ─── */
  .tool-list {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
    max-height: 300px;
    overflow-y: auto;
    padding-right: var(--space-2);
  }

  .tool-item {
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
    padding: var(--space-2) var(--space-3);
    background: var(--color-bg-secondary);
    border: 1px solid var(--color-border-primary);
    border-radius: var(--radius-md);
    font-size: var(--text-xs);
  }

  .tool-item.has-error {
    border-color: var(--color-error-bg, #FECACA);
    background: var(--color-error-light, #FEF2F2);
  }

  .tool-item-row {
    display: flex;
    align-items: center;
    gap: var(--space-2);
  }

  .tool-item .tool-timestamp {
    font-family: var(--font-mono);
    font-size: 11px;
    color: var(--color-text-tertiary);
    min-width: 40px;
    flex-shrink: 0;
    font-variant-numeric: tabular-nums;
  }

  .tool-item .tool-name {
    font-family: var(--font-mono);
    font-weight: var(--font-semibold);
    color: var(--color-text-primary);
  }

  .tool-item .tool-status {
    margin-left: auto;
    padding: 1px var(--space-2);
    border-radius: var(--radius-full);
    font-size: 10px;
    font-weight: var(--font-semibold);
    text-transform: uppercase;
    flex-shrink: 0;
  }

  .tool-item .tool-status.success {
    background: var(--color-success-light);
    color: var(--color-success);
  }

  .tool-item .tool-status.error {
    background: var(--color-error-light, #FEF2F2);
    color: var(--color-error-text, #991B1B);
  }

  .tool-item .tool-time {
    font-family: var(--font-mono);
    color: var(--color-text-tertiary);
    flex-shrink: 0;
  }

  .tool-error {
    font-size: var(--text-xs);
    color: var(--color-error-text, #991B1B);
    padding: var(--space-1) 0 0 0;
    line-height: var(--leading-relaxed);
    font-family: var(--font-mono);
    word-break: break-word;
  }

  /* ─── AI Summary (Expanded Row) ─── */
  .ai-summary-text {
    font-size: var(--text-sm);
    line-height: var(--leading-relaxed);
    color: var(--color-text-secondary);
    padding: var(--space-3);
    background: var(--color-bg-secondary);
    border-radius: var(--radius-md);
    border: 1px solid var(--color-border-primary);
  }

  .ai-summary-text.placeholder {
    color: var(--color-text-tertiary);
    font-style: italic;
  }

  /* ─── Key Topics (Expanded Row) ─── */
  .topics-list {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-2);
  }

  .topic-tag {
    display: inline-flex;
    align-items: center;
    padding: var(--space-1) var(--space-3);
    border-radius: var(--radius-full);
    font-size: var(--text-xs);
    font-weight: var(--font-medium);
    border: 1px solid;
  }

  .topic-tag.green {
    background: var(--color-success-light);
    color: var(--color-success);
    border-color: var(--color-success-light);
  }

  .topic-tag.blue {
    background: var(--color-info-light);
    color: var(--color-info);
    border-color: var(--color-info-light);
  }

  .topic-tag.amber {
    background: var(--color-accent-light);
    color: var(--color-accent);
    border-color: var(--color-accent-light);
  }

  .topic-tag.purple {
    background: #F3E8FF;
    color: #7C3AED;
    border-color: #F3E8FF;
  }

  .topic-tag.placeholder {
    background: var(--color-bg-secondary);
    color: var(--color-text-tertiary);
    border-color: var(--color-border-primary);
    font-style: italic;
  }

  /* ─── Sentiment Score (Expanded Row) ─── */
  .sentiment-score-section {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }

  .sentiment-score-display {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }

  .sentiment-score-bar {
    width: 100%;
    height: 12px;
    background: var(--color-bg-tertiary);
    border-radius: 6px;
    overflow: hidden;
  }

  .sentiment-score-fill {
    height: 100%;
    border-radius: 6px;
    transition: width var(--transition-slow);
  }

  .sentiment-score-fill.positive {
    background: linear-gradient(90deg, #22C55E, #16A34A);
  }

  .sentiment-score-fill.neutral {
    background: linear-gradient(90deg, #F59E0B, #D97706);
  }

  .sentiment-score-fill.negative {
    background: linear-gradient(90deg, #EF4444, #DC2626);
  }

  .sentiment-score-fill.unknown {
    background: var(--color-border-secondary);
  }

  .sentiment-score-label {
    font-size: var(--text-sm);
    font-weight: var(--font-semibold);
    color: var(--color-text-secondary);
  }

  /* ─── View Full Transcript Button ─── */
  .view-transcript-btn {
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    padding: var(--space-2) var(--space-4);
    background: var(--color-success);
    color: white;
    border: none;
    border-radius: var(--radius-md);
    font-family: var(--font-body);
    font-size: var(--text-sm);
    font-weight: var(--font-semibold);
    cursor: pointer;
    transition: all var(--transition-fast);
    margin-top: var(--space-2);
  }

  .view-transcript-btn:hover {
    background: var(--color-success-hover, #15803D);
    box-shadow: var(--shadow-sm);
  }

  .view-transcript-btn i {
    font-size: var(--text-sm);
  }

  /* ─── Full Transcript Section (togglable) ─── */
  .full-transcript-section {
    grid-column: 1 / -1;
    border-top: 1px solid var(--color-border-primary);
    padding-top: var(--space-4);
    margin-top: var(--space-4);
    animation: slideDown var(--duration-base) var(--ease-out) both;
  }

  /* ─── Call Metadata ─── */
  .call-meta {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: var(--space-2);
    margin-top: var(--space-2);
  }

  .meta-item {
    display: flex;
    flex-direction: column;
    gap: 1px;
    font-size: var(--text-xs);
  }

  .meta-item .meta-label {
    color: var(--color-text-tertiary);
    font-weight: var(--font-semibold);
    text-transform: uppercase;
    letter-spacing: var(--tracking-wider);
  }

  .meta-item .meta-value {
    color: var(--color-text-secondary);
    font-family: var(--font-mono);
  }

  /* ─── Pagination ─── */
  .pagination-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: var(--space-3) var(--space-4);
    border-top: 1px solid var(--color-border-primary);
    background: var(--color-bg-tertiary);
  }

  .pagination-info {
    font-size: var(--text-sm);
    color: var(--color-text-tertiary);
  }

  .pagination-info strong {
    color: var(--color-text-secondary);
    font-weight: var(--font-semibold);
  }

  .pagination-controls {
    display: flex;
    align-items: center;
    gap: var(--space-1);
  }

  .page-btn {
    min-width: 32px;
    height: 32px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0 var(--space-2);
    background: var(--color-bg-secondary);
    border: 1px solid var(--color-border-primary);
    border-radius: var(--radius-md);
    font-family: var(--font-body);
    font-size: var(--text-sm);
    font-weight: var(--font-medium);
    color: var(--color-text-secondary);
    cursor: pointer;
    transition: all var(--transition-fast);
  }

  .page-btn:hover:not(:disabled) {
    background: var(--color-bg-tertiary);
    border-color: var(--color-border-secondary);
    color: var(--color-text-primary);
  }

  .page-btn.active {
    background: var(--color-primary);
    border-color: var(--color-primary);
    color: var(--color-text-inverse);
  }

  .page-btn:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }

  .page-ellipsis {
    min-width: 32px;
    height: 32px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: var(--color-text-tertiary);
    font-size: var(--text-sm);
  }

  /* ─── Empty State ─── */
  .empty-row td {
    padding: var(--space-16) var(--space-4);
    text-align: center;
  }

  .empty-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--space-2);
  }

  .empty-icon {
    font-size: 2.5rem;
    color: var(--color-text-disabled);
    margin-bottom: var(--space-2);
  }

  .empty-title {
    font-family: var(--font-display);
    font-size: var(--text-lg);
    font-weight: var(--font-semibold);
    color: var(--color-text-secondary);
  }

  .empty-desc {
    font-size: var(--text-sm);
    color: var(--color-text-tertiary);
  }

  /* ─── Loading Overlay ─── */
  .loading-overlay {
    position: absolute;
    inset: 0;
    background: rgba(255, 255, 255, 0.7);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10;
    border-radius: var(--radius-xl);
  }

  .loading-spinner {
    width: 28px;
    height: 28px;
    border: 3px solid var(--color-border-primary);
    border-top-color: var(--color-primary);
    border-radius: 50%;
    animation: spin 0.7s linear infinite;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  /* ─── Error Banner ─── */
  .error-banner {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    padding: var(--space-3) var(--space-4);
    background: var(--color-error-light, #FEF2F2);
    border: 1px solid var(--color-error-bg, #FECACA);
    border-radius: var(--radius-md);
    color: var(--color-error-text, #991B1B);
    font-size: var(--text-sm);
  }

  .error-banner i {
    font-size: var(--text-base);
    flex-shrink: 0;
  }

  .error-banner .retry-btn {
    margin-left: auto;
    padding: var(--space-1) var(--space-3);
    background: var(--color-error-text, #991B1B);
    color: white;
    border: none;
    border-radius: var(--radius-md);
    font-family: var(--font-body);
    font-size: var(--text-xs);
    font-weight: var(--font-semibold);
    cursor: pointer;
    white-space: nowrap;
    transition: opacity var(--transition-fast);
  }

  .error-banner .retry-btn:hover {
    opacity: 0.85;
  }

  /* ─── Detail Loading ─── */
  .detail-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: var(--space-8);
    color: var(--color-text-tertiary);
    gap: var(--space-2);
    font-size: var(--text-sm);
  }

  .detail-loading .loading-spinner {
    width: 20px;
    height: 20px;
  }

  .no-data {
    font-size: var(--text-sm);
    color: var(--color-text-tertiary);
    font-style: italic;
  }

  /* ─── Responsive ─── */
  @media (max-width: 900px) {
    .stats-bar {
      grid-template-columns: repeat(2, 1fr);
    }
    .detail-inner {
      grid-template-columns: 1fr;
    }
  }

  @media (max-width: 640px) {
    .stats-bar {
      grid-template-columns: 1fr;
    }
    .filter-row {
      flex-direction: column;
      align-items: stretch;
    }
    .search-wrapper {
      min-width: auto;
    }
  }
`;
